interface ReportWebhookEntitySummary {
  id?: string
  name?: string
}

interface ReportWebhookEntityCounts {
  created?: number
  updated?: number
  skipped?: number
  createdItems?: ReportWebhookEntitySummary[]
  updatedItems?: ReportWebhookEntitySummary[]
  skippedItems?: ReportWebhookEntitySummary[]
}

interface ReportWebhookPublishSummary {
  templates?: ReportWebhookEntityCounts
  dataSources?: ReportWebhookEntityCounts
}

interface ReportWebhookActivationSummary {
  updated?: number
  notFound?: number
  unchanged?: number
  updatedItems?: ReportWebhookEntitySummary[]
  notFoundItems?: ReportWebhookEntitySummary[]
  unchangedItems?: ReportWebhookEntitySummary[]
}

interface ReportWebhookResponseBody {
  success?: boolean
  changesApplied?: number
  publish?: ReportWebhookPublishSummary
  activation?: ReportWebhookActivationSummary
}

const MAX_NAMES_SHOWN = 5

function formatItemLabel(item: ReportWebhookEntitySummary): string {
  if (item.name?.trim()) return item.name.trim()
  if (item.id) return `${item.id.slice(0, 8)}…`
  return 'unknown'
}

function formatItemList(items: ReportWebhookEntitySummary[] | undefined, max = MAX_NAMES_SHOWN): string {
  if (!items?.length) return ''
  const labels = items.map(formatItemLabel)
  if (labels.length <= max) return labels.join(', ')
  const shown = labels.slice(0, max).join(', ')
  return `${shown} +${labels.length - max} more`
}

function formatEntityCounts(label: string, counts: ReportWebhookEntityCounts): string[] {
  const lines: string[] = []
  const countParts: string[] = []
  if (counts.created) countParts.push(`${counts.created} created`)
  if (counts.updated) countParts.push(`${counts.updated} updated`)
  if (counts.skipped) countParts.push(`${counts.skipped} skipped`)
  if (countParts.length > 0) lines.push(`${label}: ${countParts.join(', ')}`)

  if (counts.createdItems?.length) {
    lines.push(`${label} created: ${formatItemList(counts.createdItems)}`)
  }
  if (counts.updatedItems?.length) {
    lines.push(`${label} updated: ${formatItemList(counts.updatedItems)}`)
  }
  if (counts.skippedItems?.length) {
    lines.push(`${label} skipped: ${formatItemList(counts.skippedItems)}`)
  }

  return lines
}

function formatPublishSummary(publish: ReportWebhookPublishSummary): string {
  const lines = [
    ...formatEntityCounts('templates', publish.templates ?? {}),
    ...formatEntityCounts('data sources', publish.dataSources ?? {}),
  ].filter(Boolean)
  return lines.join('; ')
}

function formatActivationSummary(activation: ReportWebhookActivationSummary): string {
  const lines: string[] = []
  const countParts: string[] = []
  if (activation.updated) countParts.push(`${activation.updated} updated`)
  if (activation.notFound) countParts.push(`${activation.notFound} not found`)
  if (activation.unchanged) countParts.push(`${activation.unchanged} unchanged`)
  if (countParts.length > 0) lines.push(countParts.join(', '))

  if (activation.updatedItems?.length) {
    lines.push(`updated: ${formatItemList(activation.updatedItems)}`)
  }
  if (activation.notFoundItems?.length) {
    lines.push(`not found: ${formatItemList(activation.notFoundItems)}`)
  }
  if (activation.unchangedItems?.length) {
    lines.push(`unchanged: ${formatItemList(activation.unchangedItems)}`)
  }

  return lines.join('; ')
}

export function formatWebhookResponse(body: string): { summary: string; detail?: string } {
  if (!body.trim()) {
    return { summary: '—' }
  }

  try {
    const parsed = JSON.parse(body) as ReportWebhookResponseBody
    const changes = parsed.changesApplied ?? 0
    const summary =
      changes === 0
        ? 'No changes'
        : `${changes} change${changes !== 1 ? 's' : ''}`

    let detail: string | undefined
    if (parsed.publish) {
      detail = formatPublishSummary(parsed.publish)
    } else if (parsed.activation) {
      detail = formatActivationSummary(parsed.activation)
    }

    return { summary, detail: detail || undefined }
  } catch {
    return { summary: body.length > 80 ? `${body.slice(0, 80)}…` : body }
  }
}
